import { ref, computed, inject } from 'vue';
import useAlertStore from '@/stores/alert';
import useLoaderStore from '@/stores/loader';
import { useI18n } from 'vue-i18n';

export function useCustomerAddress() {
    const addresses = ref([]);
    const defaultAddress = ref(null);
    const alertStore = useAlertStore();
    const loaderStore = useLoaderStore();
    const { t } = useI18n();
    const backendApi = inject('backendApi');

    const displayedAddresses = computed(() => {
        return addresses.value.map((address) => ({
            id: address.id,
            fullName:
                address.adrdefault?.fullName ||
                `${address.adrdefault?.name || ''} ${address.adrdefault?.lastName || ''}`.trim(),
            formattedAddress: address.adrdefault?.address || '',
            phone: address.adrdefault?.phone || '',
            email: address.adrdefault?.email || '',
            isDefault: Boolean(address.adrdefault?.default),
            originalData: address
        }));
    });

    const showList = computed(() => {
        return addresses.value.length > 0;
    });

    function setAddresses(newAddresses) {
        addresses.value = newAddresses || [];
    }

    function addAddress(newAddress) {
        if (!newAddress || !newAddress.id) {
            console.error(
                'Cannot add address: invalid address data',
                newAddress
            );
            return;
        }

        if (newAddress.name && newAddress.lastname && !newAddress.adrdefault) {
            console.error(
                'Received customer object instead of address object:',
                newAddress
            );
            return;
        }

        const existingIndex = addresses.value.findIndex(
            (addr) => addr.id === newAddress.id
        );

        if (existingIndex !== -1) {
            // Update existing address
            addresses.value[existingIndex] = newAddress;
        } else {
            // Add new address
            addresses.value.push(newAddress);
        }
    }

    function removeAddress(addressToRemove) {
        if (!addressToRemove?.id) return false;

        const confirmationMessage = t('address.delete.message');

        alertStore.addMessage(confirmationMessage, [
            {
                value: t('Yes'),
                color: 'primary',
                action: () => {
                    loaderStore.showLoader();
                    backendApi()
                        .customers()
                        .removeAddress(addressToRemove.id)
                        .then(() => {
                            addresses.value = addresses.value.filter(
                                (addr) => addr.id !== addressToRemove.id
                            );
                            alertStore.addMessage(t('address.delete.success'));
                        })
                        .catch((error) => {
                            console.error('Error deleting address:', error);
                            alertStore.addMessage(t('Error loading address'));
                        })
                        .finally(() => {
                            loaderStore.hideLoader();
                        });
                }
            },
            {
                value: t('No'),
                color: 'secondary',
            }
        ]);

        return true;
    }

    function handleAddressSaved(addressData) {
        const newAddress =
            addressData?.address || addressData?.data || addressData;

        if (!newAddress || !newAddress.id) {
            console.error('Invalid address data:', addressData);
            return;
        }

        if (!newAddress.adrdefault) {
            console.error(
                'Address object missing adrdefault property:',
                newAddress
            );
            return;
        }

        addAddress(newAddress);
    }

    function validateCustomerForAddress(customer) {
        if (!customer?.id) {
            return false;
        }
        return true;
    }

    function clearAddresses() {
        addresses.value = [];
        defaultAddress.value = null;
    }

    return {
        // State
        addresses,
        defaultAddress,
        displayedAddresses,
        showList,

        // Methods
        setAddresses,
        addAddress,
        removeAddress,
        handleAddressSaved,
        validateCustomerForAddress,
        clearAddresses
    };
}
